import classNames from 'classnames'
import {dataProps, space, type Theme, useTheme} from 'quickstart/theme'
import {truthy} from 'quickstart/types'
import {
  Children,
  type ComponentProps,
  type CSSProperties,
  Fragment,
} from 'react'
import {logger} from 'tizra'
import S from './styles.module.scss'

const log = logger('Stack')

const Item = ({as = 'div', ...props}: ComponentProps<'div'> & {as?: any}) => {
  const AsComponent = as as 'div'
  return <AsComponent data-stack-item {...props} />
}

type BK = keyof Theme['borders']
type BKx = BK | string | boolean

type SK = keyof typeof space
type SKx = SK | string

interface StackProps extends ComponentProps<'div'> {
  as?: any
  row?: boolean
  spacing?: SKx
  divided?: BKx
  capped?: BKx
  startCapped?: BKx
  endCapped?: BKx
  wrapChildren?: boolean
}

const borderStyle = (theme: Theme, bk: BKx): string =>
  !bk
    ? 'none'
    : bk === true
      ? theme.borders.fine
      : (theme.borders[bk as BK] ?? bk)

const inlineVars = <T extends {[key: `--${string}`]: string | number}>(
  vars: T,
) => vars as CSSProperties

const _Stack = ({
  as = 'div',
  children,
  capped = false,
  startCapped = capped,
  endCapped = capped,
  divided = false,
  row = false,
  spacing: sk = 'md',
  wrapChildren = true,
  ...props
}: StackProps) => {
  const AsComponent = as as 'div'
  const wrapper = as === 'ol' || as === 'ul' ? 'li' : 'div'

  const theme = useTheme()
  const vars = inlineVars({
    '--spacing': space[sk as keyof typeof space] ?? sk,
    '--divider': borderStyle(theme, divided),
    '--start-cap': borderStyle(theme, startCapped),
    '--end-cap': borderStyle(theme, endCapped),
  })

  const childArray = Children.toArray(children)
    .filter(truthy)
    .map((child, i, arr) => [
      <Fragment key={i}>
        {((i === 0 && startCapped) || (i > 0 && divided)) && <hr />}
        {wrapChildren ? <Item as={wrapper}>{child}</Item> : child}
        {i === arr.length - 1 && endCapped && <hr />}
      </Fragment>,
    ])

  return (
    <AsComponent
      {...dataProps({
        row,
        divided: !!divided,
        startCapped: !!startCapped,
        endCapped: !!endCapped,
      })}
      data-stack
      {...props}
      className={classNames(S.stack, props.className)}
      style={{...vars, ...props.style}}
    >
      {childArray}
    </AsComponent>
  )
}

export const Stack = Object.assign(_Stack, {Item})
